import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiSearch, FiFilter, FiPlus, FiEdit, FiTrash2, FiEye, FiAlertTriangle, FiX } from 'react-icons/fi';
import riskService from '../../services/riskService';
import projectService from '../../services/projectService';
import './RiskList.css';
import CommonModal from '../../services/CommonModal';
import { color } from 'framer-motion';
import { FaExclamationCircle, FaFileExcel } from 'react-icons/fa';

const RiskList = ({ projectId = null, showStatistics = true }) => {
  const navigate = useNavigate();
  const [risks, setRisks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const confirmDelete = () => {
    handleDelete(selectedRisk.id);
    setShowDeleteModal(false);
    setSelectedRisk(null);
  };
  const [filters, setFilters] = useState({
    search: '',
    project_id: projectId || '',
    risk_type: '',
    probability: '',
    impact: '',
    status: '',
    severity: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    loadRisks();
    if (showStatistics) {
      loadStatistics();
    }
  }, [filters, pagination.page, projectId]);

  const loadProjects = async () => {
    try {
      const data = await projectService.getAllProjects({ limit: 100 });
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadRisks = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        project_id: projectId,
        ...filters
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) delete params[key];
      });

      const data = await riskService.getAll(params);
      setRisks(data.data || []);
      setPagination(prev => ({
        ...prev,
        total: data.total || 0,
        pages: data.pages || 0
      }));
    } catch (error) {
      console.error('Error loading risks:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const data = await riskService.getStatistics(projectId);
      setStatistics(data.data);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      project_id: projectId || '',
      risk_type: '',
      probability: '',
      impact: '',
      status: '',
      severity: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleDelete = async (id) => {
    {
      try {
        await riskService.delete(id);
        loadRisks();
        if (showStatistics) {
          loadStatistics();
        }
      } catch (error) {
        console.error('Error deleting risk:', error);
        alert('Failed to delete risk');
      }
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleDownloadRiskList = () => {
    if (!risks || risks.length === 0) {
      alert('No risks to download');
      return;
    }

    const headers = [
      'Title', 'Project', 'Type', 'Probability', 'Impact',
      'Score', 'Severity', 'Status', 'Owner'
    ];

    const escapeCsv = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = risks.map((risk) => [
      risk.title,
      risk.project ? risk.project.name : '',
      risk.risk_type,
      risk.probability,
      risk.impact,
      risk.risk_score,
      risk.severity,
      risk.status,
      risk.owner ? `${risk.owner.first_name} ${risk.owner.last_name}` : 'Unassigned'
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCsv).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `risks-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="risk-list-container">
      {/* Statistics Cards */}
      {showStatistics && statistics && (
        <div className="risk-statistics">
          <div className="risk-stat-card">
            <div className="risk-stat-icon bg-blue-100 text-blue-600">
              <FiAlertTriangle />
            </div>
            <div className="risk-stat-content">
              <div className="risk-stat-value">{statistics.total}</div>
              <div className="risk-stat-label">Total Risks</div>
            </div>
          </div>
          <CommonModal
            isOpen={showDeleteModal}
            title="Delete Risk"
            message="Are you sure you want to delete this risk?"
            onClose={() => {
              setShowDeleteModal(false);
              setSelectedRisk(null);
            }}
            onConfirm={confirmDelete}
            type="error"
            confirmText="Delete"
            cancelText="Cancel"
          />
          <div className="risk-stat-card">
            <div className="risk-stat-icon badge-danger">
              <FiAlertTriangle />
            </div>
            <div className="risk-stat-content">
              <div className="risk-stat-value">{statistics.by_severity.Critical}</div>
              <div className="risk-stat-label">Critical</div>
            </div>
          </div>

          <div className="risk-stat-card">
            <div className="risk-stat-icon badge-warning">
              <FiAlertTriangle />
            </div>
            <div className="risk-stat-content">
              <div className="risk-stat-value">{statistics.by_severity.High}</div>
              <div className="risk-stat-label">High</div>
            </div>
          </div>

          <div className="risk-stat-card">
            <div className="risk-stat-icon bg-yellow-100 text-black">
              <FiAlertTriangle />
            </div>
            <div className="risk-stat-content">
              <div className="risk-stat-value">{statistics.by_severity.Medium}</div>
              <div className="risk-stat-label">Medium</div>
            </div>
          </div>

          <div className="risk-stat-card">
            <div className="risk-stat-icon badge-success">
              <FiAlertTriangle />
            </div>
            <div className="risk-stat-content">
              <div className="risk-stat-value">{statistics.by_severity.Low}</div>
              <div className="risk-stat-label">Low</div>
            </div>
          </div>

          <div className="risk-stat-card">
            <div className="risk-stat-icon badge-info">
              📊
            </div>
            <div className="risk-stat-content">
              <div className="risk-stat-value">{statistics.average_risk_score}</div>
              <div className="risk-stat-label">Avg Score</div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="risk-list-header">
        <div className="risk-list-title-section">
          <h2 className="risk-list-title">Risks</h2>
          <span className="risk-list-count">{pagination.total} total</span>
        </div>
        <div className="risk-list-actions">
          <button
            className="btn-filter"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FiFilter />
            Filters
          </button>
          <Link to="/app/risks/new" className="btn-primary">
            <FiPlus />
            Add Risk
          </Link>
            <button
            className="px-4 py-4 bg-white border border-green-600 text-green-600 rounded-lg flex items-center gap-2"
            onClick={handleDownloadRiskList}
            title="Download risk list"
          >
            <FaFileExcel />
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="risk-filters">
          <div className="risk-filters-grid">
            <div className="form-group">
              <label>Search</label>
              <div className="input-with-icon">
                <FiSearch className="input-icon" />
                <input
                  type="text"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Search risks..."
                  className="form-control"
                />
              </div>
            </div>

            {!projectId && (
              <div className="form-group">
                <label>Project</label>
                <select
                  name="project_id"
                  value={filters.project_id}
                  onChange={handleFilterChange}
                  className="form-control"
                >
                  <option value="">All Projects</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name} ({project.code})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Risk Type</label>
              <select
                name="risk_type"
                value={filters.risk_type}
                onChange={handleFilterChange}
                className="form-control"
              >
                <option value="">All Types</option>
                {riskService.getRiskTypes().map(type => (
                  <option key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Probability</label>
              <select
                name="probability"
                value={filters.probability}
                onChange={handleFilterChange}
                className="form-control"
              >
                <option value="">All Probabilities</option>
                {riskService.getProbabilityLevels().map(level => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Impact</label>
              <select
                name="impact"
                value={filters.impact}
                onChange={handleFilterChange}
                className="form-control"
              >
                <option value="">All Impacts</option>
                {riskService.getImpactLevels().map(level => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="form-control"
              >
                <option value="">All Statuses</option>
                {riskService.getStatusOptions().map(status => (
                  <option key={status.value} value={status.value}>
                    {status.icon} {status.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Severity</label>
              <select
                name="severity"
                value={filters.severity}
                onChange={handleFilterChange}
                className="form-control"
              >
                <option value="">All Severities</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>
          </div>
          <div className="risk-filters-actions">
            <button onClick={handleClearFilters} className="btn-secondary">
              <FiX />
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Risk List */}
      {loading ? (
        <div className="risk-list-loading">Loading risks...</div>
      ) : risks.length === 0 ? (
        <div className="risk-list-empty">

          <div className="flex flex-col items-center justify-center text-gray-500">
            <FiAlertTriangle size={48} className="text-4xl mb-2 text-gray-400" />
            <p className="text-sm font-medium">No risks found.</p>
          </div>
          {/* <Link to="/app/risks/new" className="">
            <FiPlus />
            Add First Risk
          </Link> */}
        </div>
      ) : (
        <>
          <div className="risk-list-table">
            <table>
              <thead>
                <tr>
                  <th>Risk</th>
                  <th>Type</th>
                  <th>Probability</th>
                  <th>Impact</th>
                  <th>Score</th>
                  <th>Severity</th>
                  <th>Status</th>
                  <th>Owner</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {risks.map(risk => (
                  <tr key={risk.id}>
                    <td>
                      <div className="risk-title-cell">
                        <Link to={`/app/risks/${risk.id}`} className="risk-title-link">
                          {risk.title}
                        </Link>
                        {risk.project && (
                          <span className="risk-project-name">{risk.project.name}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${riskService.getRiskTypeColor(risk.risk_type)}`}>
                        {riskService.getRiskTypeIcon(risk.risk_type)} {risk.risk_type}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${riskService.getProbabilityColor(risk.probability)}`}>
                        {risk.probability}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${riskService.getImpactColor(risk.impact)}`}>
                        {risk.impact}
                      </span>
                    </td>
                    <td>
                      <span className="risk-score">{risk.risk_score}</span>
                    </td>
                    <td>
                      <span className={`badge ${riskService.getSeverityColor(risk.severity)}`}>
                        {risk.severity}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${riskService.getStatusColor(risk.status)}`}>
                        {riskService.getStatusIcon(risk.status)} {risk.status}
                      </span>
                    </td>
                    <td>
                      {risk.owner ? (
                        <span className="risk-owner">
                          {risk.owner.first_name} {risk.owner.last_name}
                        </span>
                      ) : (
                        <span className="text-gray-400">Unassigned</span>
                      )}
                    </td>
                    <td>
                      <div className="risk-actions">
                        <Link to={`/app/risks/${risk.id}`} className="btn-icon" title="View" style={{ color: "var(--color-warning)" }}>
                          <FiEye />
                        </Link>
                        <Link to={`/app/risks/${risk.id}/edit`} className="btn-icon" title="Edit" style={{ color: "var(--color-primary)" }}>
                          <FiEdit />
                        </Link>
                        <button
                          onClick={() => {
                            setSelectedRisk(risk);
                            setShowDeleteModal(true);
                          }}
                          className="btn-icon btn-icon-danger"
                          title="Delete"
                          style={{ color: "var(--color-danger" }}
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="risk-list-pagination">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="btn-pagination"
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="btn-pagination"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RiskList;

